import { Component, EventEmitter, Input, OnInit, Output, ViewChild, NgZone } from '@angular/core';
import { take } from 'rxjs/operators';
import { TRADE_MARKET_ADDRESS, TRADE_SLIPPAGE } from 'src/app/angular-wallet-base/constants';
import { AppModeService } from 'src/app/angular-wallet-base/services/app-mode.service';
import { getSideMenuStatus } from 'src/app/angular-wallet-base/store/appSettings';
import { AppState } from 'src/app/angular-wallet-base/store/appState';
import { SubscriptionListenerComponent } from 'src/app/angular-wallet-base/SubscriptionListenerComponent';
import { IonModal, ModalController, NavController, Platform, ToastController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { SelectAssetModal } from '../../select-asset-modal/select-asset-modal.component';
import { OrderTypeComponent } from '../order-type/order-type.component';
import { ReviewOrderComponent } from '../review-order/review-order.component';
import { OneInchService } from 'src/app/angular-wallet-base/services/one-inch.service';
import { presentToast } from 'src/app/angular-wallet-base/utils';
import { TranslateService } from '@ngx-translate/core';
import { ExpiryComponent } from '../expiry/expiry.component';
import { ExpiryCustomComponent } from '../expiry-custom/expiry-custom.component';
import { NetworkService } from 'src/app/angular-wallet-base/services/network.service';
import { Logger } from 'src/app/angular-wallet-base/services/logger.service';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from 'src/app/angular-wallet-base/services/auth.service';
import { isNil } from 'src/app/angular-wallet-base/ts-util';
import { LanguageService } from 'src/app/angular-wallet-base/services/language.service';
import { RoutingService } from '../../../angular-wallet-base/services/routing.service';
import { TradeService } from 'src/app/angular-wallet-base/services/trade.service';
import { environment } from 'src/environments/environment';
import { EnvironmentService } from 'src/app/angular-wallet-base/services/environment.service';

@Component({
  selector: 'app-sub-trade',
  templateUrl: './trade.component.html',
  styleUrls: ['./trade.component.scss'],
})
export class SubTradeComponent extends SubscriptionListenerComponent implements OnInit {

  @ViewChild(IonModal) expiryDropDown: IonModal;
  @Output() showGraph: EventEmitter<boolean> = new EventEmitter();
  @Output() showAdvancedGraph: EventEmitter<{ showAdvancedGraph: boolean, pair: string }> = new EventEmitter();
  @Input() tokens: any;

  public isDesktopMode: boolean;
  public assetToTrade;
  public assetToTradeAmount = null;
  public tradeFor;
  public tradeForAmount = null;
  public orderType = 'market';
  public orderLimit = null;
  public loader: boolean = false;
  public askBidLoader: boolean = false;
  public errorMessage: string;
  public lastestTradeSummary;
  public formValidationErrors = {};
  public submitForm: boolean = false;
  public expiryDate = { type: 'day', value: 7, name: 'days' };
  public currentNetwork;
  public currentPair = new BehaviorSubject<string>('');
  public askBidPair = '';
  public currentLanguage = 'en';
  public tradeFeature = this.environmentService.trade;

  constructor(
    public appMode: AppModeService,
    private modalController: ModalController,
    private routingService: RoutingService,
    private oneInchService: OneInchService,
    private translate: TranslateService,
    private platform: Platform,
    private toast: ToastController,
    private nav: NavController,
    private networkService: NetworkService,
    protected store: Store<AppState>,
    readonly ngZone: NgZone,
    private languageService: LanguageService,
    private tradeService: TradeService,
    private environmentService: EnvironmentService
  ) {
    super(store);
    this.appMode.isDesktopMode.subscribe(mode => this.isDesktopMode = mode);
  }

  async ngOnInit() {
    this.currentNetwork = await this.networkService.getActiveAvaxNetwork();
    await this.oneInchService.initiateConnection();

    if (this.tradeFeature) {
      this.subscriptions.add(
        this.oneInchService.askBidLoader.subscribe(status => {
          this.askBidLoader = status;
        })
      );
      this.subscriptions.add(
        this.currentPair.subscribe(data => {
          if (data !== this.askBidPair) {
            this.getLatestAsksBids();
          }
        })
      );
    }

    const currentLanguage = this.languageService.getCurrentLanguage();
    this.currentLanguage = (currentLanguage && currentLanguage.length > 0) ? currentLanguage[0].value : 'en';
  }

  // select asset
  async selectAsset(selectFor: string) {
    // trade-for selection is enabled, only when asset-to-trade selection is complete
    if (selectFor === 'tradeFor' && !this.assetToTrade) {
      return;
    }

    this.lastestTradeSummary = null;
    const cssClass = (this.isDesktopMode) ? 'select-asset-modal-desktop' : 'select-asset-modal';
    const modal = await this.modalController.create({
      component: SelectAssetModal,
      componentProps: {
        actionType: 'trade',
        assetToTrade: this.assetToTrade,
        tradeFor: this.tradeFor,
        selectFor,
        orderType: this.orderType
      },
      cssClass,
      initialBreakpoint: (this.isDesktopMode) ? undefined : 0.9,
      breakpoints: (this.isDesktopMode) ? undefined : [0, 0.6, 0.9, 1],
    });
    await modal.present();
    await modal.onDidDismiss().then(response => {
      if (response && response.data && selectFor === 'assetToTrade') {
        // clear tradeFor to re-check the acceptable liquity pair
        if (response.data !== this.assetToTrade) {
          this.tradeFor = null;
          this.tradeForAmount = null;
        }
        this.assetToTrade = response.data;
      }
      if (response && response.data && selectFor === 'tradeFor') {
        this.tradeFor = response.data;
      }
      this.calculateTrade();
    });
  }

  // on swap asset
  async swapAsset() {
    if (this.assetToTrade || this.tradeFor) {
      [this.assetToTrade, this.tradeFor] = [this.tradeFor, this.assetToTrade];
    }
    this.calculateTrade();
  }

  // loader event
  async loaderEvent() {
    this.loader = true;
  }

  // on amount change
  async updateAmount(amountTO: string, event) {
    // to show error message on entering negative value as amount
    if (this.assetToTradeAmount < 0 || this.tradeForAmount < 0) {
      presentToast('trade.failed', 'trade.negative_error', this.translate, this.toast, this.platform);
      if (this.assetToTradeAmount < 0) {
        this.ngZone.run(() => {
          this.assetToTradeAmount = null;
          event.target.value = '';
        });
      } else if (this.tradeForAmount < 0) {
        this.ngZone.run(() => {
          this.tradeForAmount = null;
          event.target.value = '';
        });
      }
      this.loader = false;
      return;
    }

    if (!event.target.value && !event.detail.value) {
      return;
    }

    if (amountTO === 'assetToTradeAmount') {
      if (event.detail.value === this.assetToTradeAmount && this.orderType === 'market') {
        return;
      }
      this.assetToTradeAmount = Number(event.detail.value);
    }

    if (this.orderType === 'limit') {
      this.tradeForAmount = (amountTO === 'tradeForAmount')
        ? event.target.value
        : (this.orderLimit > 0 ? (this.orderLimit * Number(this.assetToTradeAmount)) : this.tradeForAmount);
      this.orderLimit = this.tradeForAmount > 0 ? this.tradeForAmount / Number(this.assetToTradeAmount) : null;
    }

    this.calculateTrade();
  }

  // on amount change
  async updateLimit(event) {
    if (!event.target.value) {
      return;
    }
    this.orderLimit = event.target.value;
    if (this.orderLimit < 0) {
      presentToast('trade.failed', 'trade.negative_error', this.translate, this.toast, this.platform);
      this.ngZone.run(() => {
        this.orderLimit = '';
        event.target.value = '';
      });
    }
    this.tradeForAmount = this.orderLimit * this.assetToTradeAmount;
    this.calculateTrade();
  }

  // on amount swap
  async swapAmount() {
    if (this.assetToTradeAmount > 0 || this.tradeForAmount > 0) {
      [this.assetToTradeAmount, this.tradeForAmount] = [this.tradeForAmount, this.assetToTradeAmount];
    }
  }

  // calculate trade
  async calculateTrade() {
    this.loader = true;
    this.submitForm = false;
    this.formValidationErrors['max_amount'] = false;

    // check if selected same
    if (this.assetToTrade?.symbol === this.tradeFor?.symbol) {
      this.tradeFor = null;

      if (this.tradeFeature) {
        this.showGraph.emit(true);
        this.showAdvancedGraph.emit({
          showAdvancedGraph: false,
          pair: null
        });
      }

      this.loader = false;
      return false;
    }

    // hide the graph
    if (this.assetToTrade && this.tradeFor && this.tradeFeature) {
      this.showGraph.emit(false);
      this.showAdvancedGraph.emit({
        showAdvancedGraph: true,
        pair: `${this.assetToTrade.symbol}_${this.tradeFor.symbol}`
      });
      this.currentPair.next(`${this.assetToTrade.symbol}_${this.tradeFor.symbol}`);
    }

    // check if asset trade has enough balance
    if (this.assetToTradeAmount > parseFloat(this.assetToTrade.preciseBalance)) {
      this.formValidationErrors['max_amount'] = true;
      this.loader = false;
      return false;
    }

    // check trade pair selected
    if (!this.assetToTrade || !this.tradeFor || !this.assetToTradeAmount || (this.assetToTradeAmount <= 0)) {
      this.loader = false;
      return false;
    }

    // check if same assets are selected
    if (this.assetToTrade.symbol === this.tradeFor.symbol) {
      this.tradeFor = null;
      this.loader = false;
      return false;
    }

    // get estimation value for market order
    if (this.orderType === 'market' && this.tradeFor) {
      try {
        const estimation = await this.tradeService.getEstimationForSwap(
          this.assetToTrade.assetGuid,
          this.tradeFor.assetGuid,
          +this.assetToTradeAmount,
          TRADE_SLIPPAGE
        );
        const estimationAmount = Number(estimation.price).toFixed(6);
        this.tradeForAmount = parseFloat(estimationAmount);
      } catch (error) {
        Logger.error(`calculateTrade ${error}`);
        presentToast('trade.failed', `Trade Quoted Failed - ${error?.message}`, this.translate, this.toast, this.platform, true);
        this.tradeForAmount = null;
        this.loader = false;
        return false;
      }
    }

    // check if limit order type
    if (this.orderType === 'limit') {
      if (this.orderLimit <= 0) {
        this.loader = false;
        return false;
      }
    }

    this.loader = false;
    if (
      this.assetToTrade &&
      this.assetToTradeAmount &&
      this.tradeFor &&
      this.tradeForAmount &&
      (this.tradeFeature ? this.currentPair.getValue() : true) &&
      this.orderType &&
      this.assetToTradeAmount < parseFloat(this.assetToTrade.preciseBalance)
    ) {

      this.submitForm = true;
    }
  }

  resetForm() {
    this.assetToTrade = null;
    this.assetToTradeAmount = null;
    this.tradeFor = null;
    this.tradeForAmount = null;
    this.showGraph.emit(true);
    this.showAdvancedGraph.emit({
      showAdvancedGraph: false,
      pair: null
    });
    this.submitForm = false;
  }

  async getLatestAsksBids() {
    if (this.askBidLoader === true) { return; } // handle multiple calls
    try {
      this.askBidPair = this.currentPair.getValue(); // handle multiple calls
      this.lastestTradeSummary = await this.oneInchService.getAskBids(this.assetToTrade.assetGuid, this.tradeFor.assetGuid);
    } catch (e) {
      Logger.info(e);
    }
  }

  async selectOrderType() {
    const sideMenuHidden = await this.store.select(getSideMenuStatus).pipe(take(1)).toPromise();
    const previousOrderType = this.orderType;

    let cssClass = 'trade-order-type-modal';
    cssClass += (!sideMenuHidden && this.isDesktopMode) ? ' with-side-menu' : '';
    const modal = await this.modalController.create({
      component: OrderTypeComponent,
      cssClass,
    });
    await modal.present();
    await modal.onDidDismiss().then(response => {
      if (response.data) {
        this.orderType = response.data;
        if (previousOrderType !== this.orderType) {
          this.tradeFor = null;
          this.assetToTrade = null;
          this.tradeForAmount = null;
          this.assetToTradeAmount = null;
          this.orderLimit = null;
        }

        // clear AVAX selection if limit order
        if (this.orderType === 'limit') {
          this.assetToTrade = (this.assetToTrade && this.assetToTrade.assetGuid === this.currentNetwork.NATIVE_TOKEN_ADDRESS) ? null : this.assetToTrade;
          this.tradeFor = (this.tradeFor && this.tradeFor.assetGuid === this.currentNetwork.NATIVE_TOKEN_ADDRESS) ? null : this.tradeFor;
        }
      }
    });
    this.calculateTrade();
  }

  toAdvancedGraph() {
    const currentPair = this.currentPair.getValue();
    if (currentPair) {
      try {
        const pair = TRADE_MARKET_ADDRESS[currentPair]
          ? currentPair
          : currentPair.split('_').reverse().join('_');
        this.nav.navigateForward(['trade/advanced-graph', pair]);
      } catch (error) { }
    }
  }

  async toPlaceOrder() {
    const payLoad = {
      pair: this.currentPair.getValue(),
      price: this.assetToTradeAmount,
      size: this.tradeForAmount,
      orderType: this.orderType,
      orderLimit: this.orderLimit,
      expiry: this.expiryDate
    };
    const cssClass = (this.isDesktopMode) ? 'confirm-modal-desktop' : 'confirm-modal';
    const modal = await this.modalController.create({
      component: ReviewOrderComponent,
      cssClass,
      componentProps: {
        payLoad,
        assetToTrade: this.assetToTrade,
        tradeFor: this.tradeFor
      },
      backdropDismiss: false,
      id: 'review-trade-modal',
    });
    await modal.present();
    await modal.onDidDismiss().then(async response => {
      if (!isNil(response.data)) {
        this.resetForm();
        if(response.data==='toHome') {
          await this.routingService.goHome();
        }
      }
    });
  }

  async selectExpiryTimeForTrade() {
    const cssClass = (this.isDesktopMode) ? 'expiry-order-modal-desktop' : 'expiry-order-modal';
    const modal = await this.modalController.create({
      component: ExpiryComponent,
      cssClass,
    });
    await modal.present();
    await modal.onDidDismiss().then(async response => {
      if (response.data) {
        const expiry = response.data;
        if (expiry) {
          if (expiry.type === 'custom') {
            await this.selectCustomExpiryTimeForTrade();
          } else {
            this.expiryDate = expiry;
          }
        }
      }
    });
    this.calculateTrade();
  }

  async selectCustomExpiryTimeForTrade() {
    const cssClass = (this.isDesktopMode) ? 'expiry-order-modal-desktop' : 'expiry-order-modal';
    const modal = await this.modalController.create({
      component: ExpiryCustomComponent,
      cssClass,
    });
    await modal.present();
    await modal.onDidDismiss().then(async response => {
      if (response.data) {
        const expiry = response.data;
        if (expiry && expiry.type) {
          this.expiryDate = expiry;
        }
      }
    });
    this.calculateTrade();
  }

}
